import React, { useEffect, useState, useMemo, memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FixedSizeList as List } from 'react-window'
import cn from 'classnames'
import { ReactComponent as MagnfyingGlass } from '../../assets/icons/magnifying_glass.svg'

import styles from './List.module.scss'

import OrderRow from './OrderRow'
import ConfirmModalV2 from '../ConfirmModalV2'
import useSpinner from '../../context/hooks/useSpinner'
import { getIsLoadingOrders } from '../../selectors/order'
import useGetEnvVars from '../../hooks/useGetEnvVars'
import AutoSizer from 'react-virtualized-auto-sizer'
import { CircularProgress } from '@material-ui/core'
import { areEqual } from '../../VNUtil/Memoization'
import { orderDetailsRequested, selectOrder } from '../../actions/order'
import { getIsKiosk, getTabsModeEnabled } from '../../selectors/menus'
import { APPLICATION_STRINGS } from '../../strings'

import { getIsSyncing } from '../../VNSyncManager/Selectors'
import VNLoader from '../../VNLoader/components/VNLoader'
import { getNetworkAvailableValue } from '../../utils/networkConnected'
import useOrdersList from '../../VNOrders/hooks/useOrdersList'

const ROW_HEIGHT_TABLET_PERCENT = 5.677
const ROW_HEIGHT_TABLET_PADDING = 42
const ROW_HEIGHT_MOBILE_PERCENT = 9.2
const ROW_HEIGHT_MOBILE_PADDING = 58
const ROW_HEIGHT_MOBILE_WITH_LOCATIONS = 70
const OFFLINE_ROW_HEIGHT_MOBILE = 120
const OFFLINE_ROW_HEIGHT_TABLET = 80
const OVERSCAN_COUNT = 20
const LIST_BOTTOM_PADDING = '5rem'

const OrdersList = ({ className }) => {
  const dispatch = useDispatch()
  const classNames = cn(styles.ordersList, className)
  const [showSyncingInProgress, setShowSyncingInProgress] = useState(false)
  const isSyncInProgress = useSelector(getIsSyncing)
  const isKiosk = useSelector(getIsKiosk)
  const tabsModeEnabled = useSelector(getTabsModeEnabled)
  const isLoading = useSelector(getIsLoadingOrders)

  const { isRefundReceiptsEnabled } = useGetEnvVars()
  const { stopSpinner } = useSpinner()
  const isNetworkAvailable = getNetworkAvailableValue()

  const isMobile = window.matchMedia('(max-width: 768px)').matches

  const { getCurrentOrders } = useOrdersList()
  const filteredOrders = getCurrentOrders(isRefundReceiptsEnabled)
  const hasLocations = isMobile && filteredOrders.some((order) => order.userNotes?.includes('Section'))

  /**
   * Calculate the row height to mimic the same behavior as CSS rules
   * for the row height in "react-window" List component to work
   */
  const rowHeight = useMemo(() => {
    if (isMobile) {
      const padding = hasLocations ? ROW_HEIGHT_MOBILE_WITH_LOCATIONS : ROW_HEIGHT_MOBILE_PADDING
      const mobileHeight = (window.innerHeight * ROW_HEIGHT_MOBILE_PERCENT) / 100 + padding
      // this is needed since the keyboard can pop up randomly causing the window.innerHeight to be
      // way too low causing height of each row to be mixed together.
      return mobileHeight < OFFLINE_ROW_HEIGHT_MOBILE ? OFFLINE_ROW_HEIGHT_MOBILE : mobileHeight
    }

    const tabletHeight = (window.innerHeight * ROW_HEIGHT_TABLET_PERCENT) / 100 + ROW_HEIGHT_TABLET_PADDING
    return tabletHeight < OFFLINE_ROW_HEIGHT_TABLET ? OFFLINE_ROW_HEIGHT_TABLET : tabletHeight
  }, [hasLocations, isMobile])

  useEffect(() => {
    stopSpinner()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSelectOrder = (order) => {
    const orderId = order.uuid

    if (order.wasCreatedInStadium) {
      dispatch(orderDetailsRequested(orderId))
    }

    dispatch(selectOrder(orderId))
  }

  const maybeOnSelectOrder = (order) => {
    if (isSyncInProgress) {
      setShowSyncingInProgress(true)
      return
    }

    onSelectOrder(order)
  }
  const rowRenderer = memo(({ index, style, isScrolling }) => {
    if (isScrolling) {
      return (
        <div key={index} style={style} className={styles.loader}>
          <CircularProgress size="1em" color="inherit" />
          &nbsp;Loading...
        </div>
      )
    }

    const order = filteredOrders[index]

    return (
      <div key={index} style={style}>
        <OrderRow
          order={order ?? {}}
          className={styles.row}
          key={order?.id}
          onSelect={() => maybeOnSelectOrder(order)}
          refundable={order?.refundable}
          isKiosk={isKiosk}
          paddingEnd={'200px'}
          style={style}
          hasLocations={hasLocations}
        />
      </div>
    )
  }, areEqual)

  if (isLoading && !filteredOrders.length && tabsModeEnabled && isNetworkAvailable) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}>
          <VNLoader isSidebarLoading processing standalone />
        </div>
        <span>{APPLICATION_STRINGS.LOADING}</span>
      </div>
    )
  }

  if (!filteredOrders.length) {
    return (
      <div className={styles.noOrders}>
        <span>{APPLICATION_STRINGS.NO_RESULTS}</span>
        <MagnfyingGlass className={styles.magnifyingGlass} />
      </div>
    )
  }

  return (
    <div className={classNames}>
      <div className={styles.headerRow}>
        <div>Customer</div>
        <div>Amount</div>
        <div>Order #</div>
        <div>Last Updated</div>
        <div>Status</div>
        <div></div>
      </div>

      <div className={cn(styles.list, { [styles.nonTabs]: !tabsModeEnabled })}>
        <AutoSizer>
          {({ height, width }) => (
            <List
              width={width}
              height={height}
              itemCount={filteredOrders.length}
              itemSize={rowHeight}
              overscanCount={OVERSCAN_COUNT}
              useIsScrolling={true}
              className={styles.listRenderer}
              style={{ paddingBottom: LIST_BOTTOM_PADDING, scrollbarWidth: 'none' }}
            >
              {rowRenderer}
            </List>
          )}
        </AutoSizer>
      </div>
      {showSyncingInProgress && (
        <ConfirmModalV2
          headerText={''}
          subtext={APPLICATION_STRINGS.STOP_SYNC_TO_VIEW_ORDERS}
          buttonTwoText={'OK'}
          onButtonTwoClick={() => setShowSyncingInProgress(false)}
          singleButtonOnly
        />
      )}
    </div>
  )
}

export default OrdersList
